import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { CSVLink } from 'react-csv'
import CircularProgressBar from '../CircularProgressBar'
import { getName as getAdAccountName } from '../contacts/LazyAdAccount'
import { ExportButton } from 'app/components/ui/export-button'
import { getName as getLocationName } from '../LazyLocationCell'
import { getName as getWorkspaceName } from '../LazyWorkspaceCell'
import { Modal } from '../Modal'
import { notifyError } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { logError } from 'lib/logging'

export default function ExportRows({
  loaderPropertyName,
  totalSize,
  columns,
  columnVisibility,
  search: searchProp,
  enrichRows,
}: {
  loaderPropertyName: string
  totalSize: number
  columns: ColumnDef<any, any>[]
  columnVisibility: { [k: string]: boolean }
  search?: string
  enrichRows?: (rows: any[]) => Promise<any[]>
}) {
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false)
  const [fetcherLoading, setFetcherLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const [rows, setRows] = useState<any[]>([])
  const [page, setPage] = useState(0)
  const [startPreparing, setStartPreparing] = useState(false)
  const [formattedRows, setFormattedRows] = useState<any[] | undefined>(undefined)
  const progress = useMemo(
    () =>
      Math.min(
        Math.round(totalSize <= 10000 ? ((page + 1) / (totalSize / 1000)) * 100 : ((page + 1) / (10000 / 1000)) * 100),
        100,
      ),
    [totalSize, page],
  )

  const handleModalClose = () => {
    setIsExportPanelOpen(false)
  }

  const [fetchComplete, setFetchComplete] = useState(false)

  const fetchPage = (params: URLSearchParams) => {
    setFetcherLoading(true)
    setFetchComplete(false)
    const page = Number(params.get('page') || 0)
    const limit = Number(params.get('limit') || 1000)
    const sort = params.get('sort') || undefined
    const search = searchProp || params.get('query') || params.get('filters') || undefined
    const [sortField, sortDir] = (sort || '').split(':')
    gqlFetchList(loaderPropertyName, {
      search,
      pagination: { page, limit },
      sort: sortField
        ? { field: sortField, direction: (sortDir?.toUpperCase() as 'ASC' | 'DESC') || 'DESC' }
        : undefined,
    })
      .then((data) => {
        if (data?.hits) {
          setRows((prev) => [...prev, ...(data.hits as unknown[])])
          if (data.hits.length < limit) {
            setFetchComplete(true)
          }
        } else {
          setFetchComplete(true)
        }
      })
      .finally(() => setFetcherLoading(false))
  }

  useEffect(() => {
    if (fetchComplete || rows.length >= 10000 || rows.length >= totalSize) {
      if (rows.length > 0) {
        bg(formatRows())
      } else {
        setFormattedRows([])
      }
    } else {
      setFormattedRows(undefined)
    }

    async function formatRows() {
      setFormattedRows(undefined)
      let source = rows
      if (enrichRows) {
        try {
          source = await enrichRows(rows)
        } catch (err) {
          logError(`Could not add the extra export columns`, err)
          notifyError(`Some columns could not be added to the export; downloading with the raw values instead`)
        }
      }
      setFormattedRows(
        await Promise.all(
          source?.map(async (row) =>
            Object.fromEntries(
              await Promise.all(
                Object.entries(row)
                  .filter(([key]) => columnVisibility[key])
                  ?.map(async ([key, value]) => {
                    if (key === `locationId` && !!value) {
                      const locationName = await getLocationName(value as string)
                      value = locationName
                    } else if (key === `workspaceId` && !!value) {
                      const workspaceName = await getWorkspaceName(value as string)
                      value = workspaceName
                    } else if (key === `trackableCampaignId` && !!value) {
                      const campaignName = await getAdAccountName(value as string)
                      value = campaignName
                    } else if (key === `leadOwner` && !!value) {
                      value = (value as any).name
                    } else if (key === `owner` && !!value) {
                      value = (value as any).name
                    } else if (key === `contacts` && !!value) {
                      value = (value as any)
                        .map((contact: any) => {
                          return contact.name
                        })
                        .join(`, `)
                    } else if (key.includes(`Date`) && !!value) {
                      value = moment(value).format(`MMM D, YYYY`)
                    }
                    return [key, value]
                  }),
              ).then((entries) =>
                entries?.map(([key, value]) => [
                  columns.find((col) => col.id === key)!.header,
                  ((value as any) || ``).toString(),
                ]),
              ),
            ),
          ),
        ),
      )
    }
  }, [rows.length, fetchComplete])

  useEffect(() => {
    if (isExportPanelOpen) {
      setRows([])
      setPage(0)
      setFetchComplete(false)
      if (totalSize <= 10000) {
        setStartPreparing(true)
      } else {
        setStartPreparing(false)
      }
      const params = new URLSearchParams(searchParams)
      params.set(`page`, `0`)
      params.set(`limit`, `1000`)
      fetchPage(params)
    }
  }, [totalSize, searchParams, columns, columnVisibility, isExportPanelOpen])

  useEffect(() => {
    if (!fetcherLoading && startPreparing && !fetchComplete) {
      if (rows.length < 10000 && rows.length < totalSize && rows.length > 0 && rows.length % 1000 === 0) {
        const nextPage = page + 1
        setPage(nextPage)
        const params = new URLSearchParams(searchParams)
        params.set(`page`, nextPage.toString())
        params.set(`limit`, `1000`)
        fetchPage(params)
      }
    }
  }, [rows.length, startPreparing, page, fetcherLoading, fetchComplete])

  return (
    <>
      <ExportButton onClick={() => setIsExportPanelOpen(true)} disable={totalSize === 0} />
      {/* eslint-disable-next-line deprecation/deprecation */}
      <Modal
        open={isExportPanelOpen}
        title={`Export`}
        isFullScreen={false}
        onClose={() => handleModalClose()}
        hideTitle={true}
        children={
          <>
            <div className="flex w-screen flex-col items-center justify-center md:w-[460px]">
              <div className="mt-9 w-full px-7">
                {totalSize > 10000 && !startPreparing ? (
                  <div className="mb-6 flex flex-col items-center">
                    <p className="text-body-md text-strong">
                      {`You are about to export the first 10,000 of ${totalSize.toLocaleString(
                        `en-US`,
                      )} rows. This may take a while.`}
                    </p>
                    <div className="mt-11 flex">
                      <button className="btn btn-sm btn-tertiary" onClick={() => handleModalClose()}>
                        Cancel
                      </button>
                      <button className="btn btn-sm btn-primary ml-4" onClick={() => setStartPreparing(true)}>
                        Start Export
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto mb-6 flex w-fit flex-col items-center">
                    <CircularProgressBar progress={progress} />
                    <p className="mt-6 text-heading-md">
                      {progress < 100 || !formattedRows ? `Preparing file...` : `File is ready to download`}
                    </p>

                    {progress === 100 && formattedRows ? (
                      <CSVLink
                        data={formattedRows}
                        onClick={() => handleModalClose()}
                        filename={`${loaderPropertyName}-export.csv`}
                        className="btn btn-sm btn-primary mx-auto mt-6"
                        target="_blank"
                      >
                        Download
                      </CSVLink>
                    ) : (
                      <button className="btn btn-md btn-tertiary mt-6" onClick={() => handleModalClose()}>
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        }
      />
    </>
  )
}
